<template>
  <FormCard
    :title="$t('personalInfo.title')"
    :subtitle="$t('personalInfo.subtitle')"
    icon
    iconBgClass="bg-gradient-to-br from-blue-500 to-indigo-600"
  >
    <template #icon>
      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-5" novalidate>
      <!-- Full Name -->
      <div class="form-group">
        <label for="name" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.nameLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <input
          type="text"
          id="name"
          :value="form.name"
          @input="updateField('name', $event.target.value)"
          @blur="validateField('name')"
          :placeholder="$t('personalInfo.namePlaceholder')"
          class="form-input"
          :class="{ 'error': localErrors.name || errors.name }"
          maxlength="100"
          autocomplete="name"
          required
        />
        <div v-if="localErrors.name || errors.name" class="form-error" role="alert">
          {{ localErrors.name || errors.name }}
        </div>
      </div>

      <!-- Email -->
      <div class="form-group">
        <label for="email-confirm" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.emailLabel') }} </span>
          </div>
        </label>
        <input
          type="email"
          id="email-confirm"
          :value="form.email"
          @input="updateField('email', $event.target.value)"
          @blur="validateField('email')"
          :placeholder="$t('personalInfo.emailPlaceholder')"
          class="form-input"
          :class="{ 'error': localErrors.email || errors.email }"
          maxlength="254"
          autocomplete="email"
          required
        />
        <div v-if="localErrors.email || errors.email" class="form-error" role="alert">
          {{ localErrors.email || errors.email }}
        </div>
      </div>

      <!-- Phone Number with Country Picker -->
      <div class="form-group">
        <label for="phone_number" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.phoneLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>

        <div class="phone-wrapper" @click.stop>
          <button
            type="button"
            class="phone-trigger"
            @click="!phoneLocked && toggleCountryDropdown()"
            :disabled="phoneLocked"
          >
            <img
              :src="getFlagUrl(selectedCountry.code)"
              :alt="selectedCountry.code"
              class="flag-img"
            />
            <span class="phone-dial-code">+{{ selectedCountry.dial_code }}</span>
            <svg
              :class="['phone-chevron', showCountryDropdown && 'phone-chevron--open']"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <input
            type="tel"
            id="phone_number"
            :value="form.phone_number"
            @input="!phoneLocked && updateField('phone_number', $event.target.value.replace(/\D/g, ''))"
            @blur="validateField('phone_number')"
            :placeholder="$t('personalInfo.phonePlaceholder')"
            class="phone-input"
            :class="{ 'phone-input--error': localErrors.phone_number || errors.phone_number, 'phone-input--locked': phoneLocked }"
            maxlength="15"
            inputmode="numeric"
            autocomplete="tel"
            :readonly="phoneLocked"
            required
          />
          <!-- Country Dropdown -->
          <transition name="dropdown">
            <div v-if="showCountryDropdown" class="country-dropdown">
              <div class="country-search-wrap">
                <svg class="country-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  v-model="countrySearch"
                  type="text"
                  class="country-search-input"
                  :placeholder="$t('personalInfo.searchCountry') || 'Search...'"
                  @click.stop
                  ref="countrySearchInput"
                />
              </div>
              <div class="country-list">
                <button
                  v-for="c in filteredCountries"
                  :key="c.code"
                  type="button"
                  :class="['country-item', c.code === selectedCountry.code && 'country-item--active']"
                  @click="selectCountry(c)"
                >
                  <img :src="getFlagUrl(c.code)" :alt="c.code" class="flag-img" />
                  <span class="country-item-name">{{ c.name }}</span>
                  <span class="country-item-code">+{{ c.dial_code }}</span>
                </button>
                <div v-if="filteredCountries.length === 0" class="country-empty">
                  {{ $t('personalInfo.noResults') || 'No results found' }}
                </div>
              </div>
            </div>
          </transition>
        </div>
        <div v-if="localErrors.phone_number || errors.phone_number" class="form-error" role="alert">
          {{ localErrors.phone_number || errors.phone_number }}
        </div>
        <p v-if="phoneLocked" class="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <svg class="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {{ $t('personalInfo.phoneLockedNote') }}
        </p>
      </div>

      <!-- Owner of case (optional) — when the reporter is not the victim -->
      <div class="form-group">
        <label for="owner_phone_number" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.ownerPhoneLabel') }}</span>
          </div>
        </label>
        <input
          type="tel"
          id="owner_phone_number"
          :value="form.owner_phone_number"
          @input="updateField('owner_phone_number', $event.target.value.replace(/[^\d+]/g, ''))"
          :placeholder="$t('personalInfo.ownerPhonePlaceholder')"
          class="form-input"
          maxlength="20"
          inputmode="tel"
        />
        <p class="text-xs text-gray-500 mt-1">{{ $t('personalInfo.ownerPhoneHint') }}</p>
      </div>

      <!-- Profession -->
      <div class="form-group">
        <label for="profession" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.professionLabel') }}</span>
          </div>
        </label>
        <input
          type="text"
          id="profession"
          :value="form.profession"
          @input="updateField('profession', $event.target.value)"
          @blur="validateField('profession')"
          :placeholder="$t('personalInfo.professionPlaceholder')"
          class="form-input"
          :class="{ 'error': localErrors.profession || errors.profession }"
          maxlength="100"
          autocomplete="organization-title"
          required
        />
        <div v-if="localErrors.profession || errors.profession" class="form-error" role="alert">
          {{ localErrors.profession || errors.profession }}
        </div>
      </div>

      <!-- Document Type -->
      <div class="form-group">
        <label for="id_type" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span>{{ $t('personalInfo.docTypeLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <select 
          id="id_type"
          :value="form.id_type"
          @change="updateField('id_type', $event.target.value)"
          @blur="validateField('id_type')"
          class="form-input"
          :class="{ 'error': localErrors.id_type || errors.id_type }"
          required
        >
          <option value="">{{ $t('personalInfo.docTypeSelect') }}</option>
          <option value="national_id">{{ $t('personalInfo.docTypeNationalId') }}</option>
          <option value="passport">{{ $t('personalInfo.docTypePassport') }}</option>
          <option value="residence_permit">{{ $t('personalInfo.docTypeResidence') }}</option>
        </select>
        <div v-if="localErrors.id_type || errors.id_type" class="form-error" role="alert">
          {{ localErrors.id_type || errors.id_type }}
        </div>
      </div>

      <!-- ID Number -->
      <div class="form-group">
        <label for="id_number" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
            </div>
            <span>{{ idLabel }} {{ $t('personalInfo.idNumberLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <input
          type="text"
          id="id_number"
          :value="form.id_number"
          @input="updateField('id_number', $event.target.value)"
          @blur="validateField('id_number')"
          :placeholder="idPlaceholder"
          class="form-input"
          :class="{ 'error': localErrors.id_number || errors.id_number }"
          maxlength="50"
          required
        />
        <div v-if="localErrors.id_number || errors.id_number" class="form-error" role="alert">
          {{ localErrors.id_number || errors.id_number }}
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-center gap-3 pt-4">
        <button 
          type="button" 
          @click="$emit('back')" 
          class="btn-secondary group"
        >
          <svg class="w-4 h-4 mr-1 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          {{ $t('personalInfo.backBtn') }}
        </button>
        
        <button 
          type="submit" 
          class="btn-primary group"
          :disabled="loading"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>{{ loading ? $t('personalInfo.savingBtn') : $t('personalInfo.nextBtn') }}</span>
          <svg v-if="!loading" class="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </form>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';
import { countries, getFlag } from '@/utils/countries';

export default {
  name: 'PersonalInfo',
  components: { FormCard },
  props: {
    form: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // 'phone' when the user verified via WhatsApp OTP, 'email' otherwise.
    authMethod: { type: String, default: null }
  },
  emits: ['submit', 'back', 'update:field'],
  data() {
    return {
      countries,
      selectedCountry: countries.find(c => c.code === 'CM') || countries[0],
      showCountryDropdown: false,
      countrySearch: '',
      localErrors: {
        name: '',
        email: '',
        phone_number: '',
        profession: '',
        id_type: '',
        id_number: ''
      }
    };
  },
  computed: {
    // The phone number is locked (read-only) when it is the verified OTP
    // number, so a reporter cannot change it away from the number we verified.
    phoneLocked() {
      return this.authMethod === 'phone';
    },
    filteredCountries() {
      if (!this.countrySearch.trim()) return this.countries;
      const q = this.countrySearch.toLowerCase().trim();
      return this.countries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.dial_code.includes(q) ||
        c.code.toLowerCase().includes(q)
      );
    },
    idLabel() {
      const labels = {
        'national_id': this.$t('personalInfo.docTypeNationalId'),
        'passport': this.$t('personalInfo.docTypePassport'),
        'residence_permit': this.$t('personalInfo.docTypeResidence')
      };
      return labels[this.form.id_type] || this.$t('personalInfo.idNumberDocument');
    },
    idPlaceholder() {
      const placeholders = {
        'national_id': this.$t('personalInfo.idPlaceholderNationalId'),
        'passport': this.$t('personalInfo.idPlaceholderPassport'),
        'residence_permit': this.$t('personalInfo.idPlaceholderResidence')
      };
      return placeholders[this.form.id_type] || this.$t('personalInfo.idPlaceholderDefault');
    }
  },
  methods: {
    getFlagUrl(code) {
      return getFlag(code, 40);
    },

    toggleCountryDropdown() {
      this.showCountryDropdown = !this.showCountryDropdown;
      if (this.showCountryDropdown) {
        this.countrySearch = '';
        this.$nextTick(() => {
          if (this.$refs.countrySearchInput) this.$refs.countrySearchInput.focus();
        });
      }
    },

    selectCountry(c) {
      this.selectedCountry = c;
      this.showCountryDropdown = false;
      this.countrySearch = '';
    },

    handleOutsideClick(e) {
      if (this.showCountryDropdown && !e.target.closest('.phone-wrapper')) {
        this.showCountryDropdown = false;
      }
    },

    /**
     * Build full international phone number.
     * If the user already typed the country code, don't double-prefix it.
     */
    buildFullPhone(localNumber) {
      if (!localNumber || !localNumber.trim()) return '';
      const digits = localNumber.replace(/\D/g, '').replace(/^0+/, '');
      const dialCode = this.selectedCountry.dial_code;
      if (digits.startsWith(dialCode)) {
        return '+' + digits;
      }
      return '+' + dialCode + digits;
    },

    updateField(field, value) {
      this.$emit('update:field', { field, value });
      if (this.localErrors[field]) {
        this.localErrors[field] = '';
      }
    },
    
    validateField(field) {
      const value = this.form[field];
      
      switch (field) {
        case 'name':
          if (!value || !value.trim()) {
            this.localErrors.name = this.$t('personalInfo.errors.nameRequired');
          } else if (value.trim().length < 2) {
            this.localErrors.name = this.$t('personalInfo.errors.nameMin');
          } else {
            this.localErrors.name = '';
          }
          break;
          
        case 'email':
          if (value && value.trim()) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              this.localErrors.email = this.$t('personalInfo.errors.emailInvalid');
            } else {
              this.localErrors.email = '';
            }
          } else {
            this.localErrors.email = '';
          }
          break;
          
        case 'phone_number':
          if (!value || !value.trim()) {
            this.localErrors.phone_number = this.$t('personalInfo.errors.phoneRequired');
          } else if (value.length < 9) {
            this.localErrors.phone_number = this.$t('personalInfo.errors.phoneInvalid');
          } else {
            this.localErrors.phone_number = '';
          }
          break;
          
        case 'profession':
          this.localErrors.profession = '';
          break;
          
        case 'id_type':
          if (!value) {
            this.localErrors.id_type = this.$t('personalInfo.errors.docTypeRequired');
          } else {
            this.localErrors.id_type = '';
          }
          break;
          
        case 'id_number':
          if (!value || !value.trim()) {
            this.localErrors.id_number = this.$t('personalInfo.errors.idNumberRequired');
          } else {
            this.localErrors.id_number = '';
          }
          break;
      }
      
      return !this.localErrors[field];
    },

    validateAll() {
      const fields = ['name', 'phone_number', 'id_type', 'id_number'];
      let isValid = true;
      
      fields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });
      
      if (this.form.email && this.form.email.trim()) {
        this.validateField('email');
      }
      
      this.validateField('profession');
      
      return isValid;
    },

    handleSubmit() {
      if (this.validateAll()) {
        // Emit the full phone number with country code before submitting
        const fullPhone = this.buildFullPhone(this.form.phone_number);
        this.$emit('update:field', { field: 'phone_number_full', value: fullPhone });
        this.$emit('submit');
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
}
</script>

<style scoped>
.form-group {
  @apply w-full space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-gray-700 flex items-center gap-2;
}

.form-input {
  @apply w-full px-4 py-3 bg-white text-gray-900 rounded-xl border border-gray-300 
         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
         placeholder-gray-500 hover:border-gray-400 shadow-sm;
  font-size: 16px;
}

.form-input:focus {
  @apply bg-white shadow-lg shadow-blue-500/10;
}

.form-input.error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply text-red-600 text-sm flex items-center mt-1;
}

.form-error::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  background-image: url("data:image/svg+xml,%3Csvg fill='none' stroke='%23dc2626' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'%3E%3C/path%3E%3C/svg%3E");
}

/* ── Phone / Country Picker ── */
.phone-wrapper {
  position: relative;
  display: flex;
  align-items: stretch;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  background: #fff;
  overflow: visible;
  transition: border-color 0.2s, box-shadow 0.2s;
  z-index: 20;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.phone-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.phone-wrapper:hover {
  border-color: #9ca3af;
}

.phone-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.phone-trigger:hover { background: #f9fafb; }

.flag-img {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
  flex-shrink: 0;
}

.phone-dial-code {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.phone-chevron {
  width: 14px;
  height: 14px;
  color: #9ca3af;
  transition: transform 0.2s;
}

.phone-chevron--open { transform: rotate(180deg); }

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  font-family: inherit;
  color: #111827;
  background: transparent;
  min-width: 0;
  border-radius: 0 0.75rem 0.75rem 0;
}

.phone-input::placeholder { color: #6b7280; }
.phone-input--error { background: #fef2f2; }
.phone-input--locked { background: #f3f4f6; color: #6b7280; cursor: not-allowed; }

/* ── Country Dropdown ── */
.country-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 320px;
  width: max-content;
  max-width: 400px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08);
  z-index: 9999;
  overflow: hidden;
}

.country-search-wrap {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.country-search-icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #9ca3af;
  pointer-events: none;
}

.country-search-input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #111827;
  outline: none;
  background: #f9fafb;
  transition: border-color 0.15s;
}

.country-search-input:focus { border-color: #3b82f6; background: #fff; }
.country-search-input::placeholder { color: #9ca3af; }

.country-list { max-height: 300px; overflow-y: auto; }
.country-list::-webkit-scrollbar { width: 5px; }
.country-list::-webkit-scrollbar-track { background: #f9fafb; }
.country-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

.country-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  font-size: 0.85rem;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}

.country-item:hover { background: #eff6ff; }
.country-item--active { background: #eef2ff; font-weight: 600; }
.country-item-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.country-item-code { color: #9ca3af; font-size: 0.8rem; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.country-empty { padding: 20px 14px; text-align: center; color: #9ca3af; font-size: 0.85rem; }

/* Dropdown transition */
.dropdown-enter-active { transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); }
.dropdown-leave-active { transition: all 0.15s ease; }
.dropdown-enter-from { opacity: 0; transform: translateY(-8px) scale(0.97); }
.dropdown-leave-to { opacity: 0; transform: translateY(-4px); }

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}

@media (max-width: 640px) {
  .country-dropdown { min-width: 100%; max-width: 360px; }
}
</style>